<script>
  import {
    setContext,
    getContext,
    onMount,
    getAllContexts,
    onDestroy,
  } from "svelte";
  import { Loader } from "@gl/design-system";
  import { user, language, contract, callout, Auth, _ } from "./stores/index";
  import { UsersRepository } from "./repositories/index";
  import { MicrofrontendError } from "./components/index";
  import {
    loadMicrofrontendModule,
    reportLoadFailure,
  } from "./services/microfrontend";
  import { LOCATION } from "svelte-routing";

  let config = getContext("config");

  const authClass = new Auth(user, "channel-manager");
  setContext("isAuthorized", authClass);

  const BUNDLE_URL = "/event-manager-frontend/event-manager.es.js";

  let App = null;
  let instance = null;
  let target = null;
  let loading = false;
  let loadError = null;

  const location = getContext(LOCATION);

  async function mount() {
    instance?.$destroy();
    loading = true;
    loadError = null;
    try {
      if (import.meta.env.MODE === "LOCAL_EVENT_MANAGER") {
        const module = await import("@gl/event-manager");
        App = module.default;
        return;
      }

      const module = await loadMicrofrontendModule(
        BUNDLE_URL,
        "channel-manager",
      );

      instance = new module.default({
        target,
        props: {
          apiurl: config.API_URL,
          user: $user,
          isAuthorized: authClass,
          language: $language,
          contract: $contract,
          basepath: "/channel-manager",
          repositories: {
            getUsers: UsersRepository.getUsers.bind(this, $contract?.id),
            getGroups: UsersRepository.getGroups.bind(this, $contract?.id),
          },
          path: $location.pathname + ($location.search || ""),
        },
      });
      instance.$on("callout", (e) => callout.set(e.detail.callout));
    } catch (error) {
      // A failed import and a throwing constructor both land here.
      loadError = error;
      if (error?.name !== "MicrofrontendLoadError") {
        reportLoadFailure(error, "channel-manager");
      }
    } finally {
      loading = false;
    }
  }

  onMount(mount);

  function retry() {
    mount();
  }

  $: instance?.$set({ language: $language });
  $: instance?.$set({ user: $user });
  $: instance?.$set({ contract: $contract });
  $: instance?.$set({
    repositories: {
      getUsers: UsersRepository.getUsers.bind(this, $contract?.id),
      getGroups: UsersRepository.getGroups.bind(this, $contract?.id),
    },
  });
  $: instance?.$set({ path: $location.pathname + ($location.search || "") });

  onDestroy(() => {
    instance?.$destroy();
  });
</script>

{#if loadError}
  <MicrofrontendError
    app="channel-manager"
    retrying={loading}
    on:click={retry}
  />
{:else if loading && !App}
  <div class="mfe-loading">
    <Loader />
    <span>
      {$_("microfrontend.loading", { values: { app: $_("channel-manager") } })}
    </span>
  </div>
{/if}

{#if App}
  <svelte:component
    this={App}
    apiurl={config.API_URL}
    user={$user}
    isAuthorized={authClass}
    language={$language}
    contract={$contract}
    basepath="/channel-manager"
    repositories={{
      getUsers: UsersRepository.getUsers.bind(this, $contract?.id),
      getGroups: UsersRepository.getGroups.bind(this, $contract?.id),
    }}
    on:callout={(event) => callout.set(event.detail.callout)}
  />
{/if}

{#if import.meta.env.MODE !== "LOCAL_EVENT_MANAGER"}
  <div bind:this={target} id="event-manager" />
{/if}

<style>
  #event-manager {
    height: 100%;
    max-width: 100vw;
    overflow: auto;
  }

  .mfe-loading {
    display: flex;
    gap: 12px;
    align-items: center;
  }
</style>
